import { useEffect } from 'react';
import { disconnectEcho, getEcho, isEchoEnabled } from '@shared/services';
import { useAuthStore } from '@stores/authStore';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Payload que emite el backend en el evento `notification.emitted`.
 * Es mínimo a propósito: solo lo justo para refrescar el contador.
 * @see backend Src\Notification\Infrastructure\Broadcasting\NotificationEmitted::broadcastWith()
 */
export interface NotificationEmittedPayload {
  user_id: number;
  team_id: number;
  notification_id: number | null;
}

/** Nombre del evento de broadcast declarado en el backend (`broadcastAs`). */
const EVENT_NAME = '.notification.emitted';

/**
 * Ventana mínima entre dos rellenos del contador por reconexión. Es el intervalo
 * que tenía el polling que este hook sustituye: aunque el WebSocket se caiga y
 * vuelva en bucle, la reconexión nunca pide el contador más a menudo de lo que
 * lo pedía el sondeo anterior.
 */
const RECONNECT_REFRESH_WINDOW_MS = 2 * 60 * 1000;

/**
 * Estado de la conexión WebSocket de notificaciones, expuesto por el hook.
 */
export interface NotificationSocketState {
  /** true si el cliente WS está configurado y montó una suscripción. */
  enabled: boolean;
}

/**
 * Suscribe al canal privado del usuario autenticado y refresca las queries de
 * notificaciones (contador + listado) en tiempo real cuando llega un evento
 * `notification.emitted`. Si Reverb no está configurado (sin VITE_REVERB_APP_KEY),
 * es un no-op: el contador se queda con su carga al montar y al recuperar el foco.
 *
 * Debe montarse una sola vez en un punto alto del árbol, ya autenticado
 * (ver NotificationSocketListener en App).
 */
export const useNotificationSocket = (): NotificationSocketState => {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);
  const token = useAuthStore((state) => state.token);
  const enabled = isEchoEnabled() && userId != null && Boolean(token);

  // Al hacer logout (token null) cerramos el socket para no dejarlo colgado
  // autenticado con un JWT que ya no vale.
  useEffect(() => {
    if (!token) disconnectEcho();
  }, [token]);

  useEffect(() => {
    if (!enabled || userId == null) return;

    const echo = getEcho();
    if (!echo) return;

    // El prefijo `notifications` cubre contador y listado en una sola invalidación.
    const refreshAll = () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      // Tickets Zendesk: refrescar listado/detalle activo al instante
      queryClient.invalidateQueries({ queryKey: ['zendesk-tickets'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['zendesk-ticket'], refetchType: 'active' });
    };
    // Solo el contador (1 fila): es lo que se refresca en cada reconexión, así que
    // no puede arrastrar el listado paginado ni los tickets, y se salta si ya se
    // pidió dentro de la ventana.
    const refreshCount = () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'unread-count'],
        predicate: (query) => Date.now() - query.state.dataUpdatedAt >= RECONNECT_REFRESH_WINDOW_MS,
      });
    };

    // Canal privado del usuario. Debe coincidir exactamente con el que autoriza y
    // emite el backend (routes/channels.php + NotificationEmitted): App.Models.User.{id}.
    const channelName = `App.Models.User.${userId}`;
    const channel = echo.private(channelName);

    // `subscribed` dispara en la primera suscripción y en cada reconexión del WS:
    // rellena el hueco de los eventos perdidos mientras el socket estuvo caído,
    // que antes tapaba el polling del contador.
    channel.subscribed(refreshCount);
    channel.error((error: unknown) => {
      console.error(`[Echo] error al suscribir a ${channelName} (¿auth falló?)`, error);
    });
    channel.listen(EVENT_NAME, (payload: NotificationEmittedPayload) => {
      if (Number(payload.user_id) !== Number(userId)) {
        return; // defensa: el canal es privado por usuario, pero no confiamos ciegamente.
      }
      refreshAll();
    });

    return () => {
      // Deja de escuchar y abandona el canal privado al desmontar / cambiar de usuario.
      channel.stopListening(EVENT_NAME);
      echo.leave(channelName);
    };
    // token en deps: al re-loguear/impersonar cambia el JWT y hay que re-suscribir
    // para que la re-autorización del canal use el token vigente.
  }, [enabled, userId, token, queryClient]);

  return { enabled };
};
